import React from 'react';
import classNames from 'classnames';
import { Bone } from '../../../../../src/components/Quote/QuoteSkeletons';
import { OverviewCardHeader, OverviewColumn, OverviewGrid, OverviewSurface } from './OverviewLayout';

interface SkeletonRowsProps {
  'aria-hidden'?: boolean;
  children: React.ReactNode;
  className?: string;
  height: number;
}

const SkeletonRows = ({ children, className, height, ...rest }: SkeletonRowsProps) => (
  <div {...rest} className={classNames('flex flex-col justify-between py-4', className)} style={{ minHeight: height }}>
    {children}
  </div>
);

const CardSkeleton = ({ bodyHeight, hero, rows }: { bodyHeight: number; hero?: boolean; rows: number }) => (
  <OverviewSurface>
    <OverviewCardHeader className="border-[#eef1f5]">
      <Bone className="h-5 w-32" />
      <Bone className="h-3 w-20" />
    </OverviewCardHeader>
    <SkeletonRows className={classNames('px-[1.125rem]', { 'gap-3': hero })} height={bodyHeight}>
      {hero ? <Bone className="aspect-video w-full shrink-0" /> : null}
      {Array.from({ length: rows }, (_slot, index) => (
        <Bone className={index % 3 === 2 ? 'h-3 w-2/3' : 'h-3 w-full'} key={index} />
      ))}
    </SkeletonRows>
  </OverviewSurface>
);

const Pending = ({ children, label }: { children: React.ReactNode; label: string }) => (
  <div aria-busy="true" aria-label={label} className="contents">
    {children}
  </div>
);

export const SaysPendingSkeleton = () => (
  <div aria-hidden className="h-[22.25rem] overflow-hidden rounded-[10px] lg:h-[23.5rem]">
    <Bone className="h-full w-full" />
  </div>
);

export const RankingPendingSkeleton = () => (
  <SkeletonRows aria-hidden className="opacity-10" height={118}>
    <Bone className="h-4 w-1/2" />
    <Bone className="h-3 w-full" />
    <Bone className="h-9 w-full" />
  </SkeletonRows>
);

export const EquityLeftSkeleton = () => (
  <Pending label="Loading top stories and key data">
    <CardSkeleton bodyHeight={430} hero rows={6} />
    <CardSkeleton bodyHeight={503} rows={12} />
  </Pending>
);

export const EquityCenterSkeleton = () => (
  <Pending label="Loading key events and analyst ratings">
    <SaysPendingSkeleton />
    <CardSkeleton bodyHeight={203} rows={6} />
    <CardSkeleton bodyHeight={413} rows={9} />
  </Pending>
);

export const EquityRightSkeleton = () => (
  <Pending label="Loading company details">
    <div className="h-[250px]" />
    <CardSkeleton bodyHeight={0} hero rows={2} />
    <CardSkeleton bodyHeight={146} rows={3} />
    <CardSkeleton bodyHeight={250} rows={6} />
  </Pending>
);

export const EquityOverviewSkeleton = () => (
  <div className="main-div w-full overflow-x-hidden px-4">
    <OverviewGrid>
      <OverviewColumn>
        <EquityLeftSkeleton />
      </OverviewColumn>
      <OverviewColumn>
        <OverviewSurface aria-hidden className="h-[380px] overflow-hidden">
          <Bone className="h-full w-full" />
        </OverviewSurface>
        <EquityCenterSkeleton />
      </OverviewColumn>
      <OverviewColumn>
        <EquityRightSkeleton />
      </OverviewColumn>
    </OverviewGrid>
  </div>
);
